import { useMemo } from 'react';
import { locale, t } from '../i18n/index.ts';
import { countryOptions } from '../lib/business.ts';

/**
 * The countries with a ready country pack (packages/core/src/countries.ts): tax numbers, tax,
 * money, paper, address and invoice notes. Kept in step with the server by test/country.test.ts.
 */
export const PACK_GROUPS = {
  eu: ['AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE'],
  europe: ['AL', 'AD', 'BA', 'CH', 'GB', 'IS', 'LI', 'MC', 'MD', 'ME', 'MK', 'NO', 'RS', 'SM', 'TR', 'UA', 'XK'],
  north_america: ['US', 'CA', 'MX'],
} as const;
export const READY_COUNTRIES: readonly string[] = Object.values(PACK_GROUPS).flat();

/** Every country by name in the reader's language: the ready ones first, by region. Inside a Field it takes the field's label. */
export function CountrySelect({
  value,
  onChange,
  onBlur,
  label,
  ...field
}: {
  value: string;
  onChange: (code: string) => void;
  onBlur?: () => void;
  label?: string;
  /** Set by Field, so its label and message belong to this list. */
  id?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
  'aria-required'?: boolean;
}) {
  const all = useMemo(() => countryOptions(locale(), value), [value]);
  const group = (codes: readonly string[]) => all.filter((x) => codes.includes(x.code));
  const others = all.filter((x) => !READY_COUNTRIES.includes(x.code));
  const option = (c: { code: string; name: string }) => (
    <option key={c.code} value={c.code}>
      {c.name} ({c.code})
    </option>
  );
  return (
    <select {...field} className="input" aria-label={label} value={value.toUpperCase()} onChange={(e) => onChange(e.target.value)} onBlur={onBlur}>
      {value ? null : (
        <option value="" disabled>
          {t('country.choose')}
        </option>
      )}
      <optgroup label={t('country.group_eu')}>{group(PACK_GROUPS.eu).map(option)}</optgroup>
      <optgroup label={t('country.group_europe')}>{group(PACK_GROUPS.europe).map(option)}</optgroup>
      <optgroup label={t('country.group_north_america')}>{group(PACK_GROUPS.north_america).map(option)}</optgroup>
      <optgroup label={t('country.all')}>{others.map(option)}</optgroup>
    </select>
  );
}
